import api from './api';

/**
 * Builds query params for audit report endpoints. Arrays (siteIds, visitTypes, userIds) are kept
 * as arrays so paramsSerializer can emit repeated keys (siteIds=1&siteIds=2) for ASP.NET Core.
 */
function auditReportParamsToQuery(siteIds, rest) {
  const params = { limit: rest.limit ?? 10000 };
  if (Array.isArray(siteIds) && siteIds.length > 0) params.siteIds = siteIds;
  if (rest.projectId != null) params.projectId = rest.projectId;
  if (rest.projectNo != null) params.projectNo = rest.projectNo;
  if (Array.isArray(rest.visitTypes) && rest.visitTypes.length > 0) params.visitTypes = rest.visitTypes;
  if (rest.formName != null && rest.formName !== '') params.formName = rest.formName;
  if (Array.isArray(rest.recordedByMappingIds) && rest.recordedByMappingIds.length > 0) params.recordedByMappingIds = rest.recordedByMappingIds;
  if (rest.fromDate != null && rest.fromDate !== '') params.fromDate = rest.fromDate;
  if (rest.toDate != null && rest.toDate !== '') params.toDate = rest.toDate;
  return params;
}

/** Serializes params so arrays become repeated keys (siteIds=1&siteIds=2) for ASP.NET Core [FromQuery] List binding. */
function auditReportParamsSerializer(params) {
  const parts = [];
  Object.keys(params).forEach((key) => {
    const val = params[key];
    if (Array.isArray(val)) {
      val.forEach((v) => parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`));
    } else if (val != null && val !== '') {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(val)}`);
    }
  });
  return parts.join('&');
}

const auditService = {
  /** Legacy audit trail (AuditLog table). */
  async getTrail(tableName, recordId) {
    const response = await api.get(`/audit/${tableName}/${recordId}`);
    return response.data;
  },

  async getAll() {
    const response = await api.get('/audit');
    return response.data;
  },

  /**
   * Field-level audit trail (AuditHdr + AuditDtl) for View Audit Detail.
   * @param {string} tableName - e.g. 'CRFHdr', 'CRFDtl', 'ScheduledVisits'
   * @param {string|number} recordId - Record ID (e.g. nCRFHdrNo)
   * @param {number} limit - Max number of header entries (default 100)
   * @returns {Promise<Array>} List of { details, vEvent, dRecordedOnUTC, vRecordedSign, ... }
   */
  async getAuditHdrTrail(tableName, recordId, limit = 100, options = {}) {
    const { onDate, projectId, siteId, projectCode } = options;
    const params = {
      limit,
      ...(onDate ? { onDate } : {}),
      ...(projectId != null && projectId !== '' ? { projectId } : {}),
      ...(siteId != null && siteId !== '' ? { siteId } : {}),
      ...(projectCode ? { projectCode } : {}),
    };
    const response = await api.get(`/audit/hdr/${encodeURIComponent(tableName)}/${encodeURIComponent(String(recordId))}`, {
      params,
    });
    return response.data || [];
  },

  /**
   * Audit Report: flat list of AuditDtl rows (vFieldName, vOldValue, vNewValue, vRemark, dRecordedOnUTC, nRecordedAtTimeZone, vRecordedAtOffSet, vRecordedSign).
   */
  async getAuditDetailsReport(limit = 500) {
    const response = await api.get('/audit/report', { params: { limit } });
    return response.data || [];
  },

  /**
   * Audit Report (filtered by sites and optional filters). Returns flat list for export.
   * Requires siteIds (array, at least one); optional: projectNo, visitTypes (array: SSV,SIV,SMV,SCV), formName, userIds (array), fromDate, toDate, limit.
   */
  async getAuditDetailsReportFiltered(params = {}) {
    const { siteIds, projectId, projectNo, visitTypes, formName, recordedByMappingIds, fromDate, toDate, limit = 10000 } = params;
    const queryParams = auditReportParamsToQuery(siteIds, { projectId, projectNo, visitTypes, formName, recordedByMappingIds, fromDate, toDate, limit });
    const response = await api.get('/audit/report', { params: queryParams, paramsSerializer: auditReportParamsSerializer });
    return response.data || [];
  },

  /**
   * Page-wise audit report: returns only audit rows for fields that appear on the selected page.
   * pageKey: SiteFeasibilitySelection | SiteOnboarding | VisitSchedulingPlanner | MonitoringVisitReport | MailManagement.
   * Same filters as getAuditDetailsReportFiltered (siteIds required; projectNo, visitTypes, userIds, fromDate, toDate, limit optional).
   */
  async getAuditDetailsReportByPage(params = {}) {
    const { pageKey, siteIds, projectId, projectNo, visitTypes, recordedByMappingIds, fromDate, toDate, limit = 10000 } = params;
    const queryParams = auditReportParamsToQuery(siteIds, { projectId, projectNo, visitTypes, recordedByMappingIds, fromDate, toDate, limit });
    if (pageKey != null && pageKey !== '') queryParams.pageKey = pageKey;
    const response = await api.get('/audit/report-by-page', { params: queryParams, paramsSerializer: auditReportParamsSerializer });
    return response.data || [];
  },

  /**
   * CRF Data Report: one row per workflow event with Project, SiteNo, SubmissionDate, Report Name, Status,
   * performedBy, dRecordedOnUTC, vRecordedAtOffSet (from AuditDtl).
   * Requires siteIds (array); optional projectNo, visitTypes, fromDate, toDate, limit.
   */
  async getCRFDataReport(params = {}) {
    const { siteIds, projectId, projectNo, visitTypes, fromDate, toDate, limit = 10000 } = params;
    const queryParams = auditReportParamsToQuery(siteIds, { projectId, projectNo, visitTypes, fromDate, toDate, limit });
    const response = await api.get('/audit/crf-data-report', { params: queryParams, paramsSerializer: auditReportParamsSerializer });
    return response.data || [];
  },

  /**
   * CRA Visit Report: one row per visit (same site/project/visit filters as CRF data report; no from/to).
   */
  async getCraVisitReport(params = {}) {
    const { siteIds, projectId, projectNo, visitTypes, limit = 10000 } = params;
    const queryParams = auditReportParamsToQuery(siteIds, { projectId, projectNo, visitTypes, limit });
    const response = await api.get('/audit/cra-visit-report', { params: queryParams, paramsSerializer: auditReportParamsSerializer });
    return response.data || [];
  },

  /**
   * Audit Report (paged): returns { data, pagination }.
   */
  async getAuditDetailsReportPaged(page = 1, pageSize = 25, projectId = null) {
    const response = await api.get('/audit/report', { params: { page, pageSize, ...(projectId != null ? { projectId } : {}) } });
    const body = response.data;
    return { data: body.data || [], pagination: body.pagination || {} };
  },

  /**
   * Site field-level audit (dedicated endpoint): AuditHdr + AuditDtl + SiteMst by nSiteNo and vFieldName.
   * @param {number|string} siteNo - Site number (SiteMst.nSiteNo)
   * @param {string} fieldName - Field name (e.g. SiteDescription, InvestigatorDesc)
   * @param {number} limit - Max rows (default 100)
   * @returns {Promise<Array>} List of { vOldValue, vNewValue, vFieldName, vEvent, dRecordedOnUTC, vRecordedSign, ... }
   */
  async getSiteFieldAudit(siteNo, fieldName, limit = 100, options = {}) {
    const { projectId, siteId, projectCode } = options;
    const params = {
      limit,
      ...(projectId != null && projectId !== '' ? { projectId } : {}),
      ...(siteId != null && siteId !== '' ? { siteId } : {}),
      ...(projectCode ? { projectCode } : {}),
    };
    const response = await api.get(`/audit/site/${encodeURIComponent(String(siteNo))}/field/${encodeURIComponent(fieldName)}`, {
      params,
    });
    return response.data || [];
  }
};

export default auditService;
